
import React, { Component } from 'react';

import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';

import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Grid from '@material-ui/core/Grid';

import { Link as RouterLink } from 'react-router-dom';

import MathJax from 'react-mathjax2';

export default class Home extends Component {

  render = () => {
    return (
  <Typography component="div" style={{ padding: 8 * 3 }}>
    <Typography component="div" align="justify" gutterBottom>
      Statistical verification of temporal properties has received increasing attention during the past decade. 
      Compared to the symbolic approach, statistical model checkers are usually more scalable to large-scale 
      real-world problems with complicated stochastic behavior. The general idea of statistical verification 
      is to treat the problem of checking a temporal formula on a probabilistic system as a hypothesis testing 
      problem. By drawing sample behaviors from the underlying probabilistic system, the satisfaction of the 
      specification can be inferred with high confidence.
    </Typography>
    <Typography component="div" align="justify" gutterBottom>
      Most statistical model checking algorithms crucially rely on independent Monte Carlo sampling. Specifically, the underlying 
      probabilistic system is <em>simulated</em> to generate a sample path, and a new sample is drawn in the same manner in each 
      round. Consequently, the samples are independent and identically distributed (i.i.d.).
      In STMC, we consider two different sampling techniques: <em>stratified</em> and <em>antithetic</em>.
      In our experiment, these two techniques can significantly reduce the number of samples as examples
      in our <Link component={RouterLink} to="/benchmarks">benchmarks</Link> page shows.
    </Typography>

    <ExpansionPanel key='1' defaultExpanded={true} TransitionProps={{ unmountOnExit: false }}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" style={{ backgroundColor: '#EEEEEE' }}>
          <Typography><b>Stratified Sampling</b></Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <Typography component="div">
            <Typography component="div" align="justify" gutterBottom>
              Stratified sampling is an approach to generate negatively correlated random variables. 
              By choosing a proper representation of the Markov chain {mjax('M')}, the stratified sampling technique can be applied to generate 
              semantically negatively correlated sample paths, reducing the sampling cost for statistically verifying a fraction of temporal 
              formulas.
            </Typography>
            <Typography component="div" align="justify" gutterBottom>
              The stratified sampling algorithm generates {mjax('m')} sample paths simultaneously. 
              Let {mjax('[0,1)=[0,\\frac{1}{m}]\\cup\\cdots\\cup[\\frac{m-1}{m},1)')} be a partition of {mjax('[0,1)')}. 
              At each time {mjax('t')}, a sample is drawn from each sub-interval.
              To avoid correlation between steps, we first generate a uniformly random 
              permutation {mjax('\\pi')} on {mjax('\\{0,\\ldots,n-1\\}')} at every step, and then assign the 
              sub-interval {mjax('[\\frac{\\pi(i)}{m},\\frac{\\pi(i)+1}{m})')} to the {mjax('i')}-th path.
              The random seeds of the {mjax('m')}-stratified sample paths are repellent to each other at each time {mjax('t')}, 
              as no more than one of them can occupy the same sub-interval.
              Therefore, these sample paths are repellent to each other at each time {mjax('t')}, in the sense that the probability 
              of any two of them visiting the same state of the Markov chain is no more than (and usually less than) that of two 
              independent samples. 
              The approach summarized in the following algorithm.
            </Typography>
            <Typography gutterBottom>&nbsp;</Typography>
            <Typography component="div" align="justify" gutterBottom style={{margin:'0px'}}>
            <Grid container spacing={0} direction='row' alignItems='center' justify='center'>
              <Grid item>
                <img src={process.env.PUBLIC_URL + "/images/fig-stratified.png"} alt="Stratified Sampling Algorithm" width='400'/>
              </Grid>
            </Grid>
            </Typography>
          </Typography>
        </ExpansionPanelDetails>
      </ExpansionPanel>


      <ExpansionPanel key='2' defaultExpanded={true} TransitionProps={{ unmountOnExit: false }}>
        <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" style={{ backgroundColor: '#EEEEEE' }}>
          <Typography><b>Antithetic Sampling</b></Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <Typography component="div">
            <Typography component="div" align="justify" gutterBottom>
              Antithetic sampling is another approach to generate negatively correlated random variables. 
              The antithetic sampling technique can also be employed to generate pairs of semantically negatively correlated sample paths and 
              reduce the sampling cost for statistical verification.
            </Typography>
            <Typography component="div" align="justify" gutterBottom>
              The antithetic sampling algorithm only generates {mjax('2')} sample paths simultaneously.
              Starting from step {mjax('0')}, 
                the algorithm uniformly chooses a random number {mjax('d')} from the interval {mjax('[0,1)')} on even steps, and
                uses {mjax('1-d')} as the random number on the odd steps.
              Similar to the stratified sampling, 
                to avoid correlation between steps, we first generate a uniformly random 
                permutation {mjax('\\pi')} on {mjax('\\{0,1\\}')} at every step, and then assign the 
                sub-interval {mjax('[\\frac{\\pi(i)}{2},\\frac{\\pi(i)+1}{2})')} to the {mjax('i')}-th path ({mjax('i\\in\\{0,1\\}')}).
              Again, similar to the stratified sampling, 
                the random seeds of the {mjax('2')}-stratified sample paths are repellent to each other at each time {mjax('t')}.
              Therefore, these sample paths are repellent to each other at each time {mjax('t')}, in the sense that the probability 
              of any two of them visiting the same state of the Markov chain is no more than (and usually less than) that of two 
              independent samples. 
              The approach summarized in the following algorithm.
            </Typography>
            <Typography gutterBottom>&nbsp;</Typography>
            <Typography component="div" align="justify" gutterBottom style={{margin:'0px'}}>
            <Grid container spacing={0} direction='row' alignItems='center' justify='center'>
              <Grid item>
                <img src={process.env.PUBLIC_URL + "/images/fig-antithetic.png"} alt="Antithetic Sampling Algorithm" width='275'/>
              </Grid>
            </Grid>
            </Typography>
          </Typography>
        </ExpansionPanelDetails>
      </ExpansionPanel>

  </Typography>
    );
  }
}

function mjax(fml) {
  return  (
    <MathJax.Context input='tex'>
      <MathJax.Node inline>{fml}</MathJax.Node>
    </MathJax.Context>
  );
}